// Chamada de edge function que não perde a mensagem da função.
//
// O supabase-js descarta o corpo em qualquer status fora do 2xx: `data` vem
// null e `error` é um FunctionsHttpError com o texto fixo "Edge Function
// returned a non-2xx status code". A resposta de verdade fica pendurada em
// `error.context`, e é lá que está o que a função escreveu — o motivo em
// português, a lista de campos inválidos, o status.
//
// Em 09/09/2026 isso fez a tela de Cotação mostrar a MESMA frase para dois
// problemas sem relação: a MAG respondendo 501 ("falta a MAG configurar o
// modelo de proposta do nosso CNPJ") e a Azos respondendo 400 por causa de uma
// altura em centímetros. Duas causas, uma frase, nenhuma pista.

import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";

/** O que o supabase-js aceita como corpo — o mesmo tipo, sem repetir a união. */
type CorpoDaChamada = Parameters<typeof supabase.functions.invoke>[1] extends
  | { body?: infer B }
  | undefined
  ? B
  : never;

/** O texto genérico do supabase-js. Serve de último recurso, nunca de resposta. */
const GENERICA = /non-2xx status code/i;

type CorpoDeErro = {
  erro?: unknown;
  message?: unknown;
  error?: unknown;
  /** Algumas funções mandam a lista de campos além da frase. */
  problemas?: unknown;
};

/**
 * A mensagem que a função escreveu, ou null quando ela não escreveu nenhuma.
 *
 * Lê o corpo por `clone()` para não gastar o stream de quem for ler depois.
 */
export const mensagemDaFuncao = async (error: unknown): Promise<string | null> => {
  const resposta = (error as { context?: unknown } | null)?.context;
  if (!(resposta instanceof Response)) return null;
  let corpo: CorpoDeErro | null = null;
  try {
    corpo = (await resposta.clone().json()) as CorpoDeErro;
  } catch {
    // Corpo vazio ou não-JSON: tenta como texto puro antes de desistir.
    try {
      const texto = (await resposta.clone().text()).trim();
      return texto && !GENERICA.test(texto) ? texto : null;
    } catch {
      return null;
    }
  }
  const frase = corpo?.erro ?? corpo?.message ?? corpo?.error;
  return frase ? String(frase) : null;
};

/**
 * Texto pronto para a tela: prefere o que a função disse, cai na mensagem do
 * erro e, se nem isso houver, diz o status em vez de "algo deu errado".
 */
export const descreverFalha = async (error: unknown, quando: string): Promise<string> => {
  const daFuncao = await mensagemDaFuncao(error);
  if (daFuncao) return daFuncao;

  const bruta = (error as { message?: string } | null)?.message;
  if (bruta && !GENERICA.test(bruta)) return bruta;

  const status = ((error as { context?: unknown } | null)?.context as Response | undefined)?.status;
  // Sem corpo e sem mensagem útil: o status é a única informação real que
  // sobrou, e escondê-lo atrás de "erro inesperado" só atrasa quem for depurar.
  return status ? `${quando}: a função retornou ${status} sem detalhes` : quando;
};

/**
 * `supabase.functions.invoke` com a mensagem certa no erro.
 *
 * `quando` descreve a operação em uma frase curta e minúscula ("não foi
 * possível carregar a cotação"), usada só quando não há nada melhor.
 */
export const invocarFuncao = async <T>(
  nome: string,
  opcoes: { body?: CorpoDaChamada; quando: string },
): Promise<{ dados: T | null; erro: string | null }> => {
  try {
    const { data, error } = await supabase.functions.invoke(nome, { body: opcoes.body });
    // Função que responde 200 com `{ erro }` no corpo: acontece, e a frase dela
    // continua valendo mais que qualquer coisa que a tela invente.
    if (data && typeof data === "object" && "erro" in data) {
      return { dados: null, erro: String((data as { erro: unknown }).erro) };
    }
    if (error) return { dados: null, erro: await descreverFalha(error, opcoes.quando) };
    return { dados: data as T, erro: null };
  } catch (e) {
    return { dados: null, erro: await descreverFalha(e, opcoes.quando) };
  }
};
